import type { ReplayPresentationStep } from '../rendering/types';
import { REPLAY_STEP_INTERVAL_MS } from './feature-presentation';

export interface ReplayFrameSink {
  present(step: ReplayPresentationStep, index: number, total: number): void;
}

export interface ReplayClock {
  setTimeout(callback: () => void, delayMs: number): ReturnType<typeof globalThis.setTimeout>;
  clearTimeout(handle: ReturnType<typeof globalThis.setTimeout>): void;
}

const DEFAULT_CLOCK: ReplayClock = {
  setTimeout: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
  clearTimeout: (handle) => globalThis.clearTimeout(handle),
};

/** Presents stored frames only. It deliberately has no evaluation or RNG port. */
export class ReplayPresentationController {
  private index = 0;
  private autoplay = false;
  private timer: ReturnType<typeof globalThis.setTimeout> | null = null;

  constructor(
    private readonly steps: readonly ReplayPresentationStep[],
    private readonly sink: ReplayFrameSink,
    private readonly clock: ReplayClock = DEFAULT_CLOCK,
  ) {}

  get currentIndex(): number { return this.index; }
  get isAutoplaying(): boolean { return this.autoplay; }

  show(index = this.index): void {
    if (this.steps.length === 0) return;
    this.index = Math.max(0, Math.min(this.steps.length - 1, Math.trunc(index)));
    this.sink.present(this.steps[this.index]!, this.index, this.steps.length);
  }

  previous(): void { this.stop(); this.show(this.index - 1); }
  next(): void { this.stop(); this.show(this.index + 1); }

  start(): void {
    if (this.steps.length < 2 || this.autoplay) return;
    if (this.index >= this.steps.length - 1) this.index = 0;
    this.autoplay = true;
    this.show();
    this.schedule();
  }

  stop(): void {
    this.autoplay = false;
    if (this.timer !== null) this.clock.clearTimeout(this.timer);
    this.timer = null;
  }

  destroy(): void { this.stop(); }

  private schedule(): void {
    if (!this.autoplay) return;
    this.timer = this.clock.setTimeout(() => {
      this.timer = null;
      if (!this.autoplay) return;
      if (this.index >= this.steps.length - 1) {
        this.stop();
        return;
      }
      this.index += 1;
      this.show();
      this.schedule();
    }, REPLAY_STEP_INTERVAL_MS);
  }
}
